/* eslint-disable max-lines-per-function */
import { isNull } from 'lodash';
import { ReactElement, useCallback, useEffect } from 'react';

import { CurrentHealthcareProviderInvoiceIntegrationEnabledQuery } from '@dreryk/egabinet-invoice-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { useCqrsGet } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, Dropdown } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { useVisitSettlementStatusGlobalState } from '~libs.shared.visit-settlement';

import { VisitDetailsFormBean } from '../../shared/form/visitDetails/visitDetails.types';
import { performVisitSettlementCqrsFind } from '../../shared/performVisitSettlementCqrsFind';
import { useVisitSettlementAction } from '../../shared/useVisitSettlementAction';
import { visitSettlementTranslationKeys } from '../../translations';

const t = visitSettlementTranslationKeys.extensions.appointments.dropdownOption;

type UseVisitSettlementDropdownOptionConfig = {
   appointmentId: number;
   practitionerId: PractitionerId;
   ownedByCurrentUser: boolean;
   visitDetails: Omit<VisitDetailsFormBean, 'practitioner'>;
};

export type UseVisitSettlementDropdownOptionReturnFn = {
   visitSettlementPaymentModal: ReactElement | null;
   visitSettlementAdjustmentModal: ReactElement | null;
   renderVisitSettlementDropdownItem: () => ReactElement | null;
};

export const useVisitSettlementDropdownOption = (
   config: UseVisitSettlementDropdownOptionConfig
): UseVisitSettlementDropdownOptionReturnFn => {
   const { appointmentId, practitionerId, visitDetails, ownedByCurrentUser } = config;
   const { getStatus } = useVisitSettlementStatusGlobalState();
   const invoiceCreateAvailable = protectFeature('creatingInvoices');

   const settlingMedicalServicesPerformedByOneselfAvailable = protectFeature(
      'settlingMedicalServicesPerformedByOneself'
   );
   const settlingMedicalServicesPerformedByAnyPractitionerAvailable = protectFeature(
      'settlingMedicalServicesPerformedByAnyPractitioner'
   );
   const privateMedicalServicesReadOnlyAvailable = protectFeature('privateMedicalServicesReadOnly');

   const { data: invoiceProviderIntegrationEnabled } = useCqrsGet<boolean>(
      () => new CurrentHealthcareProviderInvoiceIntegrationEnabledQuery(),
      {
         enabled: invoiceCreateAvailable,
      }
   );

   const {
      visitUnsettled,
      setVisitUnsettled,
      setVisitSettlement,
      visitSettlementAdjustmentModal,
      visitSettlementPaymentModal,
      disabled,
      label,
      id,
      openModal,
   } = useVisitSettlementAction({
      invoiceProviderIntegrationEnabled: invoiceProviderIntegrationEnabled,
      ownedByCurrentUser: ownedByCurrentUser,
      visitDetails: visitDetails,
      appointmentId: appointmentId,
      practitionerId: practitionerId.asNumber(),
      adjustmentLabel: t.adjustment.label,
      paymentLabel: t.payment.label,
   });

   useEffect(() => {
      const status = getStatus(appointmentId);
      setVisitUnsettled(!status || status === 'UNSETTLED');
   }, [appointmentId, setVisitUnsettled, getStatus]);

   return {
      renderVisitSettlementDropdownItem: useCallback((): ReactElement | null => {
         if (isNull(visitUnsettled)) {
            return null;
         }

         if (
            privateMedicalServicesReadOnlyAvailable ||
            (!settlingMedicalServicesPerformedByOneselfAvailable &&
               !settlingMedicalServicesPerformedByAnyPractitionerAvailable)
         ) {
            return null;
         }

         return (
            <Dropdown.Item
               key={'visitSettlement'}
               testId={id}
               disabled={disabled}
               onClick={async (hide) => {
                  try {
                     const visitSettlement = await performVisitSettlementCqrsFind(appointmentId);
                     setVisitSettlement(visitSettlement);
                     openModal();
                  } catch (error) {
                     dispatchSnackbarNegativeEvent(t.errors.unexpectedError);
                  } finally {
                     hide();
                  }
               }}
            >
               {label}
            </Dropdown.Item>
         );
      }, [
         appointmentId,
         visitUnsettled,
         settlingMedicalServicesPerformedByOneselfAvailable,
         settlingMedicalServicesPerformedByAnyPractitionerAvailable,
         privateMedicalServicesReadOnlyAvailable,
         id,
         label,
         disabled,
         setVisitSettlement,
         openModal,
      ]),
      visitSettlementAdjustmentModal: visitSettlementAdjustmentModal,
      visitSettlementPaymentModal: visitSettlementPaymentModal,
   };
};
